import {
    Colors,
    CYCLE_AUDIENCE_FILTER_LAYOUT,
    CYCLE_AUDIENCE_FILTER_TYPOGRAPHY,
    FIXED_ICON_SIZES,
    Icon,
    Text,
    type MultiSelectAccent,
    type OptionProps,
} from '@amelio/ui-library';

import { AudienceFilterConjunction } from './AudienceFilterConjunction';
import { AudienceFilterRow } from './AudienceFilterRow';
import { useAudienceFilterRows } from './useAudienceFilterRows';

import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import type { CompanyAttributeMap } from '@/utils/company';

import { useAmelioTranslations } from '@/hooks';
import { getOptionLabelText } from '@/utils/optionLabel';

const FILTER_LIST = [
    'flex flex-col',
    CYCLE_AUDIENCE_FILTER_LAYOUT.listTop,
    CYCLE_AUDIENCE_FILTER_LAYOUT.listGap,
].join(' ');

const ADD_ACTION = [
    'inline-flex w-fit items-center self-start transition-opacity hover:opacity-80',
    'focus-visible:ring-primary-800 focus-visible:ring-2 focus-visible:outline-none',
    CYCLE_AUDIENCE_FILTER_LAYOUT.removeRadius,
    CYCLE_AUDIENCE_FILTER_LAYOUT.addGap,
].join(' ');

interface ChangeAttributeParams {
    rowId: string;
    previousName?: string;
}

interface RemoveRowParams {
    rowId: string;
    attributeName?: string;
}

export interface AudienceFilterListProps {
    companyAttributes: OptionProps[];
    selectedCompanyAttributes: CompanyAttributeMap;
    filters: SelectedFilters;
    accent: MultiSelectAccent;
    isReadOnly: boolean;
    onSelectCompanyAttribute: (value: string) => void;
    onSelectCompanyAttributeOption: (params: OnSelectCompanyAttributeOption) => void;
}

export const AudienceFilterList = ({
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    accent,
    isReadOnly,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
}: AudienceFilterListProps) => {
    const { t } = useAmelioTranslations();
    const { rows, addRow, dropRow, assignAttribute } =
        useAudienceFilterRows(selectedCompanyAttributes);

    const attributeNamed = (attributeName: string | null) =>
        attributeName === null
            ? undefined
            : Object.values(selectedCompanyAttributes ?? {}).find(
                  (attribute) => attribute.name === attributeName,
              );

    const optionsFreeForRow = (rowId: string) => {
        const takenNames = rows.filter((row) => row.id !== rowId).map((row) => row.attributeName);

        return companyAttributes.filter(
            (option) => !takenNames.includes(getOptionLabelText(option.label)),
        );
    };

    const changeAttribute =
        ({ rowId, previousName }: ChangeAttributeParams) =>
        (nextName: string) => {
            if (previousName === nextName) {
                return;
            }

            if (previousName) {
                onSelectCompanyAttribute(previousName);
            }

            onSelectCompanyAttribute(nextName);
            assignAttribute({ rowId, attributeName: nextName });
        };

    const removeRow =
        ({ rowId, attributeName }: RemoveRowParams) =>
        () => {
            if (attributeName) {
                onSelectCompanyAttribute(attributeName);
            }

            dropRow(rowId);
        };

    return (
        <div
            className={FILTER_LIST}
            data-testid={'audience-filter-list'}
        >
            {rows.map((row, index) => {
                const attribute = attributeNamed(row.attributeName);

                return (
                    <div
                        key={row.id}
                        className={CYCLE_AUDIENCE_FILTER_LAYOUT.rowTop}
                    >
                        {index > 0 && <AudienceFilterConjunction />}

                        <AudienceFilterRow
                            rowId={row.id}
                            attribute={attribute}
                            attributeOptions={optionsFreeForRow(row.id)}
                            filters={filters}
                            accent={accent}
                            isReadOnly={isReadOnly}
                            canRemove={rows.length > 1}
                            onAttributeChange={changeAttribute({
                                rowId: row.id,
                                previousName: attribute?.name,
                            })}
                            onRemove={removeRow({
                                rowId: row.id,
                                attributeName: attribute?.name,
                            })}
                            onSelectValue={onSelectCompanyAttributeOption}
                        />
                    </div>
                );
            })}

            {!isReadOnly && rows.length < companyAttributes.length && (
                <button
                    type={'button'}
                    className={ADD_ACTION}
                    data-testid={'audience-filter-add'}
                    onClick={addRow}
                >
                    <Icon
                        name={'essential-add'}
                        size={FIXED_ICON_SIZES.sm}
                        color={Colors.CYCLE_ACTION_INK}
                        ariaHidden={true}
                    />

                    <Text
                        color={Colors.CYCLE_ACTION_INK}
                        className={CYCLE_AUDIENCE_FILTER_TYPOGRAPHY.action}
                    >
                        {t('cycleEvaluationCycleInformation.audience.addAttribute')}
                    </Text>
                </button>
            )}
        </div>
    );
};
